import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Search, Camera, BookOpen } from 'lucide-react';
import { useVlbLookup } from '@/hooks/useVlbLookup';
import { useKatalogAufloesung } from '@/hooks/useKatalogAufloesung';
import { katalogNummerErkennen } from '@/lib/katalognummer';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { CopyButton } from '@/components/ui/CopyButton';
import { isbnAnzeige, isbnGueltig, isbnNackt } from '@/lib/isbn';
import { Cover } from '@/components/ui/Cover';
import { TitelSuche } from './TitelSuche';
import { LieferbarkeitZeile } from './Lieferbarkeit';
import { DigitalMarke } from '@/components/ui/DigitalMarke';
import { useToast } from '@/lib/toast';
import { ApiError } from '@/lib/api';
import { leerePosition } from '@/types/bestellung';
import type { KatalogTreffer, PositionInput, VlbTitel } from '@/types/bestellung';
import { FOKUS_RING } from '@/lib/fokus';

type Props = {
  onAdd: (p: PositionInput) => void;
  /** data-tour-Name fuer die Einfuehrungstour. */
  anker?: string;
  onScannerOpen?: () => void;        // SPEC-004 Erweiterungspunkt: html5-qrcode kommt spaeter
  // Optional kontrolliert — siehe FreitextInput (Befund 43).
  wert?: string;
  onWertChange?: (v: string) => void;
  /**
   * Meldet, ob gerade ein Treffer zur Bestaetigung ansteht. Der Rahmen
   * braucht das, um Esc abzufangen: solange die Vorschau offen ist,
   * verwirft Esc nur den Treffer und schliesst nicht das ganze Sheet (B50).
   */
  onReviewOffen?: (offen: boolean) => void;
};

function makeUid(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
}

export function IsbnInput({
  onAdd,
  anker,
  onScannerOpen,
  wert,
  onWertChange,
  onReviewOffen
}: Props) {
  const [intern, setIntern] = useState('');
  const kontrolliert = wert !== undefined && onWertChange !== undefined;
  const isbn = kontrolliert ? wert : intern;
  const setIsbn = kontrolliert ? onWertChange : setIntern;
  const [review, setReview] = useState<VlbTitel | null>(null);
  /**
   * Wann die Lieferbarkeits-Auskunft entstanden ist (A24).
   *
   * Kommt aus der Antwort, nicht aus dem Titel-Objekt: sie gilt fuer den
   * ganzen Abruf. Sie faellt zusammen mit der Vorschau — ein Stand ohne
   * die Auskunft, zu der er gehoert, waere fuer sich genommen sinnlos.
   */
  const [reviewStand, setReviewStand] = useState<string | null>(null);
  /**
   * Die Rückfrage bei erfolgloser Suche (E9).
   *
   * Bis hierher entstand in diesem Fall wortlos eine Position mit dem
   * Titel „[Freitext] ISBN 9788466358293 (nicht in VLB)". Zwei Fehler in
   * einem: niemand wurde gefragt, und an der Stelle, wo der Titel steht,
   * stand eine Maschinenmeldung — wer die Bestellung später aufschlug,
   * konnte nicht mehr wissen, welches Buch gemeint war.
   *
   * Das VLB ist ein Verzeichnis des deutschsprachigen Buchhandels. Dass
   * es ein spanisches Taschenbuch, ein Antiquariatsstück, eine Postkarte
   * oder ein Magazin nicht kennt, ist kein Fehler, sondern der Normalfall
   * für einen guten Teil dieses Sortiments.
   */
  const [nachfrage, setNachfrage] = useState<string | null>(null);
  /** Ist der zweite Weg — die Titelsuche — aufgeklappt? (A23.4) */
  const [titelSucheOffen, setTitelSucheOffen] = useState(false);
  /** E-Book statt Buch: mit diesem Titel startet die Titelsuche gleich. */
  const [sucheVorgabe, setSucheVorgabe] = useState<string | null>(null);
  const [nfTitel, setNfTitel] = useState('');
  const [nfAutor, setNfAutor] = useState('');
  const nfTitelRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /**
   * Welche Nummer zuletzt hinausging (A17).
   *
   * Der Waechter gegen die zweite Anfrage. Er haelt den NACKTEN Wert:
   * "978-3-7725-2891-0" und "9783772528910" sind dieselbe Nummer, und wer
   * beim Tippen die Striche nachtraegt, hat deshalb nichts Neues gefragt.
   *
   * Eine Ref, kein State: der Wert steuert keine Anzeige, und als State
   * loeste jede Fortschreibung eine Neuzeichnung aus — mitten im Tippen.
   */
  const zuletztGesucht = useRef<string | null>(isbnGueltig(wert) ? isbnNackt(wert) : null);
  /**
   * Steht noch ein Enter aus, das zu einer Suche der Automatik gehoert? (A17.2)
   *
   * Der Handscanner tippt die Ziffern und schickt ein Enter hinterher.
   * Frueher loeste dieses Enter die Suche aus; seit der Automatik ist die
   * Suche schon unterwegs, wenn es ankommt — und dann entscheidet die
   * Antwortzeit des VLB darueber, was es tut: Ist die Antwort noch nicht
   * da, verpufft es; ist sie da, uebernimmt es den Treffer, den niemand
   * gesehen hat. Ein Ablauf, der vom Netz abhaengt, ist kein Ablauf.
   *
   * Deshalb ist dieses eine Enter vergeben: Es gehoert zur Suche, so wie
   * bisher, und tut sonst nichts. Der Rhythmus am Geraet bleibt damit
   * exakt der alte — erstes Enter sucht, zweites uebernimmt —, nur dass
   * das erste jetzt nichts mehr ausloesen muss.
   */
  const enterGehoertZurSuche = useRef(false);
  const lookup = useVlbLookup();
  /**
   * Katalognummern (K3): "120-042" statt einer ISBN.
   *
   * Aufgelöst wird NUR bei Enter oder Knopf, nie durch die Automatik:
   * "120-0" und "120-04" sind unterwegs zu "120-042" selbst gültige
   * Nummern — eine Automatik löste mitten im Tippen die falsche auf.
   * Die ISBN hat diese Mehrdeutigkeit nicht; sie ist erst mit gültiger
   * Prüfziffer vollständig (A17).
   */
  const aufloesung = useKatalogAufloesung();
  const [katalogTreffer, setKatalogTreffer] = useState<KatalogTreffer | null>(null);
  const toast = useToast();

  // Der Rahmen fängt Esc ab, solange hier etwas zur Bestätigung ansteht —
  // Treffer wie Rückfrage. Sonst schlösse Esc das ganze Sheet (B50).
  useEffect(() => {
    onReviewOffen?.(review !== null || nachfrage !== null || katalogTreffer !== null);
  }, [review, nachfrage, katalogTreffer, onReviewOffen]);

  /**
   * Suchen, sobald die ISBN vollstaendig und gueltig ist (A17).
   *
   * Der Knopf war der einzige SICHTBARE Weg zur Suche, und die Frage des
   * Betreibers war die richtige: Warum drueckt jemand einen Knopf, der
   * nichts entscheidet? Die Nummer im Feld sagt selbst, wann sie fertig
   * ist.
   *
   * WARUM NICHT BEI JEDEM ZEICHEN
   *   Fuer "978", "9783", "97837" waere je eine VLB-Anfrage hinausgegangen
   *   — elf sinnlose bis zur zwoelften, bei einem bezahlten Dienst.
   *
   * WARUM NICHT NACH ZEICHENZAHL ODER WARTEZEIT
   *   Dreizehn Ziffern sind noch keine ISBN: "9783772528911" hat dreizehn
   *   und ist keine. Eine Wartezeit haette dasselbe Problem und zusaetzlich
   *   ein zweites — sie feuert auch, wenn jemand mitten im Tippen
   *   nachdenkt. Ausloeser ist die Gueltigkeit, gerechnet von `isbn3`.
   *
   * WARUM GENAU EINE ANFRAGE JE NUMMER
   *   Wer die letzte Ziffer loescht und neu tippt, kommt zweimal an
   *   derselben gueltigen Nummer vorbei. Der Waechter in machLookup()
   *   haelt fest, was zuletzt hinausging; hier steht deshalb keine
   *   zweite Bedingung.
   *
   * Der Knopf bleibt: er ist der zweite Versuch, wenn das VLB nicht
   * antwortet, und der Weg, wenn die Automatik nicht greift.
   */
  useEffect(() => {
    // Jede Aenderung im Feld macht ein noch offenes Scanner-Enter
    // gegenstandslos: es gehoerte zur vorigen Nummer.
    enterGehoertZurSuche.current = false;
    if (!isbnGueltig(isbn)) return;
    const vorher = zuletztGesucht.current;
    machLookup();
    // Nur wenn wirklich eine Anfrage hinausging, ist ein Enter vergeben.
    // Wer eine Ziffer loescht und wieder tippt, loest keine aus — und
    // sein Enter soll dann uebernehmen, nicht verpuffen.
    if (zuletztGesucht.current !== vorher) enterGehoertZurSuche.current = true;
    // machLookup() ist bei jeder Neuzeichnung eine andere Funktion und
    // haengt selbst an `isbn`. In der Abhaengigkeitsliste stuende sie
    // hier nur als Platzhalter fuer denselben Wert; was doppelte
    // Anfragen verhindert, ist der Waechter in der Funktion, nicht diese
    // Liste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isbn]);

  // Wenn die Rückfrage aufgeht, gehört der Fokus ins Titelfeld: auf dem
  // Telefon spart das einen Tipp, am Rechner kann man sofort schreiben.
  useEffect(() => {
    if (nachfrage !== null) nfTitelRef.current?.focus();
  }, [nachfrage]);

  function focusInput(): void {
    setIsbn('');
    inputRef.current?.focus();
  }

  /**
   * Gehoert die stehende Vorschau noch zu dem, was im Feld steht? (A-01)
   *
   * Verglichen wird nackt: getippt kommt eine ISBN auch mal mit Strichen
   * an, gespeichert und zurueckgeliefert wird sie ohne. "978-3-7725-2891-0"
   * und "9783772528910" sind dieselbe Nummer und muessen es auch hier sein.
   */
  function vorschauGehoertZurEingabe(t: VlbTitel): boolean {
    return isbnNackt(isbn) !== '' && isbnNackt(isbn) === isbnNackt(t.isbn);
  }

  /**
   * Der Feldinhalt aendert sich — damit ist alles darunter alt.
   *
   * Befund A-01 (#25): `machLookup()` setzte `setReview(null)` nur im
   * Erfolgsfall. Nach einem fehlgeschlagenen Lookup blieb die Vorschau des
   * VORIGEN Treffers stehen und sah aus wie ein frischer. Wer dann
   * "Uebernehmen" drueckte, legte das alte Buch an.
   *
   * Hier faellt sie schon beim Tippen, nicht erst beim naechsten Lookup:
   * Sobald die Nummer im Feld eine andere ist, ist die Vorschau darunter
   * eine zweite Wahrheit ueber dieselbe Zeile (Designprinzip 4).
   *
   * BEFUND A32 (#43): Genau dasselbe galt fuer die RUECKFRAGE eine Tuer
   * weiter — sie blieb stehen und behielt die alte Nummer. Wer danach
   * einen Titel eintippte und Enter drueckte, legte eine Position mit
   * der ALTEN ISBN und dem NEUEN Titel an. Auf dem Bildschirm sieht so
   * eine Zeile vollkommen in Ordnung aus; bestellt wird das falsche
   * Buch, und auffallen kann es erst im Wareneingang — oder nie, weil
   * der Scan die ISBN sucht und sie ja "passt".
   *
   * WARUM SCHLIESSEN UND NICHT UEBERNEHMEN
   *   Die Rueckfrage ist die Antwort auf EINE bestimmte Suche: "diese
   *   Nummer kennt das VLB nicht". Aendert sich die Frage, ist die
   *   Antwort ungueltig. Die neue Nummer einfach zu uebernehmen saehe
   *   aus, als haette das System sie geprueft — hat es nicht, und eine
   *   unbelegte Behauptung ist schlimmer als eine geschlossene Klappe.
   *   Ausserdem waeren zwei Wege fuer denselben Fehlertyp zwei
   *   Wahrheiten (Fallstrick 15); A-01 schliesst, also schliesst A32
   *   auch.
   *
   * UND NICHT STUMM
   *   Anders als bei der Vorschau geht hier Getipptes verloren: Titel
   *   und Autor standen schon im Kasten. Wer das wortlos verschwinden
   *   sieht, sucht seinen Text — deshalb die Meldung. Sie kommt genau
   *   einmal je Rueckfrage, nicht bei jedem Zeichen: danach ist
   *   `nachfrage` null.
   */
  function isbnGeaendert(v: string): void {
    setIsbn(v);
    if (review !== null) {
      setReview(null);
      setReviewStand(null);
    }
    // Dieselbe A-01-Regel für den Katalogtreffer: sobald im Feld eine
    // andere Nummer steht, ist die Vorschau darunter eine zweite
    // Wahrheit. Stumm wie bei der VLB-Vorschau — Getipptes geht hier
    // nicht verloren.
    if (katalogTreffer !== null) {
      setKatalogTreffer(null);
    }
    if (nachfrage !== null) {
      nachfrageSchliessen();
      // 'error' und nicht 'success': Der Melder kennt genau zwei Arten
      // (lib/toast.tsx). Von den beiden ist das die, die aufmerken
      // laesst — Coral, und `role="alert"`, also wird sie auch
      // vorgelesen. Fehlgeschlagen ist hier nichts; verloren gegangen
      // schon, und genau das soll niemand uebersehen.
      toast('error', 'Rückfrage verworfen — die ISBN hat sich geändert.');
    }
  }

  function nachfrageSchliessen(): void {
    setNachfrage(null);
    setNfTitel('');
    setNfAutor('');
  }

  // Scanner-Fluss ohne Maus (B50): der Scanner tippt die ISBN und schickt
  // ein Enter hinterher, das den Lookup ausloest. Das zweite Enter
  // uebernimmt den Treffer, Esc verwirft ihn. Der Handler sitzt am
  // Rahmen, damit er auch greift, wenn der Fokus auf einem der Knoepfe
  // der Vorschau steht.
  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    // Auf einem Knopf soll Enter den Knopf ausloesen, nicht diesen
    // Kurzweg — sonst uebernaehme "Abbrechen" den Treffer.
    const aufKnopf = (e.target as HTMLElement).tagName === 'BUTTON';
    if (e.key === 'Enter' && !aufKnopf) {
      e.preventDefault();
      if (enterGehoertZurSuche.current && nachfrage === null) {
        // Das Enter des Scanners (A17.2). Die Automatik hat die Suche
        // schon losgeschickt; dieses Enter hat seine Aufgabe damit
        // erfuellt und wird hier verbraucht — es uebernimmt nichts und
        // fragt nichts zweites nach. Das naechste Enter uebernimmt den
        // Treffer wie eh und je.
        enterGehoertZurSuche.current = false;
        return;
      }
      if (nachfrage !== null) {
        // Enter im Titelfeld uebernimmt — aber nur, wenn dort etwas
        // steht. Sonst entstuende die leere Position, die E9 gerade
        // verhindern soll.
        if (nfTitel.trim() !== '') nachfrageUebernehmen();
      } else if (katalogTreffer !== null && katalogGehoertZurEingabe(katalogTreffer)) {
        // Der Katalog-Rhythmus ist der der ISBN: erstes Enter loest die
        // Aufloesung aus, zweites uebernimmt den Treffer.
        katalogUebernehmen(katalogTreffer);
      } else if (review !== null && vorschauGehoertZurEingabe(review)) {
        // E-Book statt Buch (03.11.2026): Ein digitaler Titel wird nie
        // nebenbei übernommen. Der Handscanner schickt sein Enter
        // ungefragt — hier muss ein Mensch ausdrücklich „Trotzdem" sagen.
        if (!review.digital) uebernehmen(review);
      } else {
        // Der Handscanner ist eine Tastatur: er tippt die Ziffern und
        // schickt ein Enter hinterher. Beim zweiten Scan stand hier frueher
        // nur `review !== null` — der Kurzweg uebernahm den Treffer des
        // ERSTEN Scans, fuer die zweite ISBN ging nie ein Lookup hinaus,
        // und das Feld war danach leer. Ein Buch doppelt, eines gar nicht
        // (A-01, Fall 2). Gehoert die Vorschau nicht zur Eingabe, wird
        // gesucht statt uebernommen.
        //
        // Seit A17 sucht die Automatik schon bei der letzten Ziffer, und
        // dieses Enter kommt direkt danach. Es bleibt trotzdem stehen:
        // Es traegt den Fall, in dem die Automatik nicht greift (ISBN mit
        // krummer Pruefziffer, Nummer von aussen ins Feld gesetzt), und
        // es bleibt der Weg fuer einen erneuten Versuch nach einem
        // Fehlschlag. Zweimal gefragt wird deshalb nicht: beide Wege
        // gehen durch denselben Waechter in machLookup(), und wer zuerst
        // kommt, fragt — der andere schweigt. Welcher von beiden der
        // erste ist, spielt damit keine Rolle.
        machLookup();
      }
      return;
    }
    if (e.key === 'Escape' && (review !== null || nachfrage !== null || katalogTreffer !== null)) {
      // Das Schliessen des Sheets verhindert der Rahmen ueber
      // onReviewOffen — hier bleibt nur das Verwerfen.
      e.preventDefault();
      setReview(null);
      setReviewStand(null);
      setKatalogTreffer(null);
      nachfrageSchliessen();
      inputRef.current?.focus();
    }
  }

  /**
   * Die eine Suche, die alle drei Wege nehmen: Automatik, Enter, Knopf.
   *
   * `erzwingen` unterscheidet den ausdruecklichen Knopfdruck von den
   * beiden anderen. Ohne ihn schluckt der Waechter jede Anfrage nach
   * einer Nummer, die gerade schon hinausging — genau das, was den Scan
   * davor bewahrt, zweimal zu fragen (A17.2). Der Knopf muss darueber
   * hinwegkommen: er ist der zweite Versuch, wenn das VLB nicht
   * antwortet, und der Weg, wenn die Automatik nicht greift.
   */
  function machLookup(erzwingen = false, nummer?: string): void {
    const trimmed = (nummer ?? isbn).trim();
    if (trimmed === '') return;
    // Katalognummern (K3) VOR dem ISBN-Weg. Die beiden Muster sind
    // fremd zueinander — eine Katalognummer ist nie eine gültige ISBN
    // und umgekehrt —, die Reihenfolge entscheidet also keinen
    // Konflikt, sie erspart der Katalognummer nur den ISBN-Wächter:
    // der rechnet mit nackten Ziffern und kennt keine Striche.
    const katalogNr = katalogNummerErkennen(trimmed);
    if (katalogNr !== null) {
      if (erzwingen) enterGehoertZurSuche.current = false;
      katalogAufloesen(katalogNr);
      return;
    }
    const nackt = isbnNackt(trimmed);
    if (!erzwingen && nackt !== '' && zuletztGesucht.current === nackt) return;
    // Wer den Knopf drueckt, hat gehandelt: ein noch offenes Enter aus
    // einer Automatik-Suche ist damit gegenstandslos und darf dem
    // naechsten Enter nicht die Uebernahme wegnehmen.
    if (erzwingen) enterGehoertZurSuche.current = false;
    zuletztGesucht.current = nackt === '' ? null : nackt;
    // Vor dem Suchen aufraeumen, nicht nur nach einem Treffer. Damit ist
    // JEDER Fehlerzweig darunter — invalid_isbn, not_found, VLB nicht
    // erreichbar, Netz weg — automatisch mit abgedeckt, und es kann kein
    // neuer hinzukommen, der die alte Vorschau wieder stehen laesst (A-01).
    setReview(null);
    setReviewStand(null);
    lookup.mutate(trimmed, {
      onSuccess: (data) => {
        setReview(data.titel);
        setReviewStand(data.stand_vom ?? null);
      },
      onError: (err) => {
        // Ein Fehlschlag ist keine Antwort. Waere der Waechter danach
        // gesetzt geblieben, haette Enter auf derselben Nummer nichts
        // mehr getan — und der Handscanner hat keinen Knopf.
        zuletztGesucht.current = null;
        if (err instanceof ApiError) {
          if (err.code === 'invalid_isbn') {
            toast('error', 'ISBN ungültig.');
            return;
          }
          if (err.code === 'not_found') {
            // E9: fragen, nicht raten. Die Position entsteht erst, wenn
            // ein Mensch den Titel eingetippt hat.
            //
            // Die ISBN wird hier auf nackte Ziffern gebracht: getippt
            // kommt sie auch mal mit Strichen an, gespeichert wird sie
            // ohne (CLAUDE.md). Der Server hat sie im Lookup schon als
            // syntaktisch gültig abgenommen — 'not_found' gibt es nur für
            // eine echte ISBN, die das VLB nicht führt.
            setNachfrage(isbnNackt(trimmed));
            return;
          }
          toast('error', 'VLB nicht erreichbar.');
          return;
        }
        toast('error', 'Lookup fehlgeschlagen.');
      }
    });
  }

  /**
   * Die Position aus der Rückfrage (E9).
   *
   * Der Titel kommt vom Menschen und steht deshalb dort, wo der Titel
   * hingehört — ohne Vorsatz. Das Merkzeichen an der Position sagt
   * dasselbe, und ein zweites Mal Maschinentext im Titelfeld war genau
   * der Befund. `FreitextInput` hielt es früher anders und setzte ein
   * „[Freitext] " davor; seit dem Betreiber-Befund zum Präfix tut es das
   * ebenfalls nicht mehr.
   *
   * `ist_freitext` bleibt trotzdem true — es heißt „von Hand erfasst",
   * nicht „ohne Nummer". Die ISBN geht mit: ohne sie fände der
   * Wareneingang-Scan (E4) das Buch später nicht wieder.
   */
  function nachfrageUebernehmen(): void {
    const titel = nfTitel.trim();
    if (nachfrage === null || titel === '') return;
    const autor = nfAutor.trim();
    onAdd({
      ...leerePosition(makeUid()),
      isbn: nachfrage,
      titel,
      autor: autor === '' ? null : autor
    });
    nachfrageSchliessen();
    focusInput();
  }

  /**
   * Ein Treffer der Titelsuche wurde gewaehlt (A23.4).
   *
   * Hier entsteht KEINE Position aus den Suchdaten. Die ISBN geht ins
   * Feld und laeuft danach den gewoehnlichen ISBN-Weg: erst der bringt
   * den Preis (die Suchantwort traegt keinen, #83), erst er fuellt den
   * Cache, und erst er zeigt die Vorschau, die der Mensch bestaetigt.
   * Zwei Wege, die dieselbe Position unterschiedlich fuellen, waeren
   * zwei Wahrheiten (Designprinzip 4).
   *
   * `machLookup` bekommt die Nummer als Argument statt sie aus dem
   * State zu lesen: `setIsbn` wirkt erst mit der naechsten Zeichnung,
   * bis dahin stuende im State die alte Eingabe. Die Automatik in
   * useEffect fragt danach nicht noch einmal — der Waechter in
   * machLookup haelt die Nummer bereits fest.
   */
  function trefferWaehlen(nummer: string): void {
    const nackt = isbnNackt(nummer);
    setIsbn(nackt);
    setTitelSucheOffen(false);
    machLookup(true, nackt);
    inputRef.current?.focus();
  }

  /**
   * Gehört der stehende Katalogtreffer noch zur Eingabe? (A-01)
   *
   * Verglichen wird kanonisch: "Nr. 120-42" und "120-042" sind dieselbe
   * Nummer, so wie bei der ISBN mit und ohne Striche.
   */
  function katalogGehoertZurEingabe(t: KatalogTreffer): boolean {
    return katalogNummerErkennen(isbn) === t.nummer;
  }

  function katalogAufloesen(nummer: string): void {
    // Vor dem Suchen aufräumen — dieselbe A-01-Regel wie in machLookup:
    // jeder Fehlerzweig darunter ist damit abgedeckt, keine alte
    // Vorschau bleibt stehen.
    setReview(null);
    setReviewStand(null);
    setKatalogTreffer(null);
    aufloesung.mutate(nummer, {
      onSuccess: (data) => setKatalogTreffer(data.treffer),
      onError: (err) => {
        if (err instanceof ApiError && err.code === 'not_found') {
          // Kein Rückfrage-Kasten wie bei E9: Die ISBN steht auf dem
          // Buch und ist auch ohne VLB richtig — eine unbekannte
          // Katalognummer dagegen ist mit hoher Wahrscheinlichkeit
          // verhört oder vertippt. Anlegen soll man sie nicht.
          toast('error', `Nr. ${nummer} steht in keinem importierten Katalog.`);
          return;
        }
        toast('error', 'Katalog-Auflösung fehlgeschlagen.');
      }
    });
  }

  /**
   * Die Position aus dem Katalogtreffer (K3).
   *
   * Der KATALOGPREIS GEHT NICHT MIT — Entscheidung K1: er ist der Stand
   * vom Drucktermin. Angezeigt wird er in der Vorschau; preis_cent der
   * Position bleibt leer wie bei einer Handeingabe.
   *
   * ist_freitext wie beim Shopify-Import (lib/shopify.php): ohne ISBN
   * true, mit ISBN false. Ohne ISBN entsteht die Position mit dem
   * Katalogtitel und ohne Nummer — nicht die stille Maschinentext-
   * Position, die E9 abgeschafft hat: der Titel hier ist ein echter,
   * und ein Mensch hat ihn ausdrücklich übernommen.
   */
  function katalogUebernehmen(t: KatalogTreffer): void {
    onAdd({
      ...leerePosition(makeUid()),
      isbn: t.isbn,
      titel: t.titel,
      autor: t.autor,
      verlag_text: t.verlag,
      ist_freitext: t.isbn === null
    });
    setKatalogTreffer(null);
    focusInput();
  }

  function uebernehmen(t: VlbTitel): void {
    // verlag_id bleibt leer: die Zuordnung macht der Server beim
    // Speichern. Hier stand frueher ein Vergleich auf Gleichheit, der
    // "dtv Verlagsgesellschaft mbH & Co. KG" nicht auf "dtv" brachte —
    // und eine zweite Zuordnungslogik neben der serverseitigen war (A6).
    const pos: PositionInput = {
      ...leerePosition(makeUid()),
      isbn: t.isbn,
      titel: t.titel ?? `Titel ohne Bezeichnung (${t.isbn})`,
      autor: t.autor,
      verlag_text: t.verlag_text,
      preis_cent: t.preis_cent,
      cover_url: t.cover_url,
      ist_freitext: false
    };
    onAdd(pos);
    setReview(null);
    setReviewStand(null);
    focusInput();
  }

  return (
    <div className="space-y-2" data-tour={anker} onKeyDown={handleKeyDown}>
      {/* Umbrechen statt quetschen (K11).
          ------------------------------------------------------------
          Zwei Dinge trieben die Zeile über die Bildschirmbreite:

          `flex-1` allein lässt das Feld NICHT beliebig schrumpfen — ein
          Flex-Element hat `min-width: auto`, und ein <input> bringt über
          sein `size`-Attribut rund 180 Pixel Eigenbreite mit. Dazu ein
          Knopf mit dem Wort „Lookup". Gemessen ragte die Zeile 89 Pixel
          über den Rand; gescrollt wurde dann im Formular, nicht im
          Dokument — weshalb die alte Querlauf-Messung nichts fand.

          Jetzt: `min-w-0` nimmt dem Feld die Eigenbreite, und unter
          768 Pixeln bekommt es eine eigene Zeile (`basis-full`), die
          Knöpfe rücken darunter. Dieselbe Form wie im
          Beschaffungs-Formular. */}
      <div className="flex flex-wrap gap-2">
        {/* KEIN inputMode="numeric" mehr (K3, Fallstrick 18): Das Feld
            nimmt jetzt auch Katalognummern ("120-042"), und die brauchen
            den Bindestrich — den es auf der iOS-Zifferntastatur nicht
            gibt. Die E44-Ausnahme trug die ISBN nur, weil es zu ihr die
            EAN-13 als zweiten Weg gibt; zur Katalognummer gibt es
            keinen. Ein Feld ohne inputMode ist unbequem, eines mit dem
            falschen ist kaputt. */}
        <input
          ref={inputRef}
          type="text"
          value={isbn}
          onChange={(e) => isbnGeaendert(e.target.value)}
          placeholder="ISBN scannen oder Katalognummer eingeben"
          className={`min-h-[44px] min-w-0 flex-1 basis-full rounded-md border border-line bg-cream px-3 py-2.5 text-sm ${FOKUS_RING} md:basis-auto`}
        />
        {/* In der umgebrochenen Zeile füllen die Knöpfe die Breite —
            ein schmaler Knopf linksbündig unter einem vollbreiten Feld
            sieht aus wie ein Rest, nicht wie eine Wahl. Ab 768 Pixeln
            stehen sie wieder in ihrer natürlichen Breite neben dem Feld. */}
        <Button
          variant="primary"
          className="flex-1 md:flex-none"
          onClick={() => machLookup(true)}
          disabled={lookup.isPending || aufloesung.isPending || isbn.trim() === ''}
        >
          {lookup.isPending || aufloesung.isPending ? <Spinner /> : <Search className="h-4 w-4" />}
          Lookup
        </Button>
        {/* E13: dasselbe Symbol wie im Suchfeld des Dashboards. Beide
            Knöpfe öffnen dieselbe Kamera; zwei Zeichen für eine Handlung
            sind eine Zumutung, wenn man zwischen den Ansichten wechselt.
            Auch die Beschriftung ist dieselbe — der Anker-Wächter und die
            Tour sprechen von „der Kamera". */}
        {onScannerOpen && (
          <button
            type="button"
            onClick={onScannerOpen}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-md border border-line px-3 py-2 text-ink-soft hover:border-akzent hover:text-akzent md:flex-none"
            aria-label="Mit der Kamera scannen"
            title="QR vom Zettel oder Buch-Barcode scannen"
          >
            <Camera className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Der zweite Weg (A23.4, #83).
          ------------------------------------------------------------
          Am Tresen wird nach Titel und Autor gefragt, nicht nach einer
          Nummer. Bis hierher gab es dafür keinen Weg im System.

          Zugeklappt, weil die ISBN der schnellere Weg bleibt, solange
          eine da ist: Der Scanner tippt sie in einer Sekunde. Der
          Schalter steht trotzdem sichtbar darunter und nicht in einem
          Menü — was man nicht sieht, sucht man auch nicht. */}
      <button
        type="button"
        onClick={() => setTitelSucheOffen((o) => !o)}
        aria-expanded={titelSucheOffen}
        className={`inline-flex min-h-[44px] items-center gap-2 rounded-md px-2 text-sm text-akzent hover:text-akzent-stark ${FOKUS_RING}`}
      >
        <BookOpen className="h-4 w-4" aria-hidden />
        {titelSucheOffen ? 'Titelsuche schließen' : 'Keine ISBN? Nach Titel suchen'}
      </button>

      {titelSucheOffen && (
        <TitelSuche key={sucheVorgabe ?? ''} onWaehlen={trefferWaehlen} vorgabe={sucheVorgabe ?? undefined} />
      )}

      {/* Die Rückfrage bei erfolgloser Suche (E9).
          ------------------------------------------------------------
          Bewusst ein Kasten an derselben Stelle wie die Treffer-Vorschau
          und KEIN Dialog. Drei Gründe am Code:

          Erstens sitzt sie damit unter dem Feld, in das gerade getippt
          oder gescannt wurde — die Augen sind dort.

          Zweitens erbt sie die Tastatur-Regelung von B50 mitsamt
          `onReviewOffen`: Esc verwirft die Rückfrage und schließt nicht
          das ganze Erfassungs-Sheet. Ein zweiter Radix-Dialog IM Sheet
          brächte einen zweiten Fokus-Käfig und dieselbe Esc-Frage noch
          einmal.

          Drittens berührt das die Kamera-Mechanik an keiner Stelle: das
          Scan-Fenster ist längst zu, wenn die Rückfrage aufgeht. */}
      {nachfrage !== null && (
        <div className="space-y-2 rounded-md border border-gold bg-cream-warm p-3">
          <p className="font-serif text-base text-ink">
            Zu dieser ISBN wurde nichts gefunden
          </p>
          <p className="text-xs text-ink-soft">
            Das Verzeichnis lieferbarer Bücher führt vor allem den
            deutschsprachigen Buchhandel. Fremdsprachiges, Antiquarisches,
            Postkarten und Magazine stehen dort oft nicht. Trag den Titel
            von Hand ein — die ISBN bleibt an der Position.
          </p>
          <div className="flex items-center gap-1 text-xs text-ink-faint">
            <span className="tabular-nums">ISBN {isbnAnzeige(nachfrage)}</span>
            <CopyButton value={nachfrage} label={`ISBN ${nachfrage} kopieren`} />
          </div>

          <label className="block text-xs text-ink-soft">
            Titel
            <input
              ref={nfTitelRef}
              type="text"
              value={nfTitel}
              maxLength={500}
              onChange={(e) => setNfTitel(e.target.value)}
              placeholder="Wie heißt das Buch?"
              className={`mt-1 block min-h-[44px] w-full min-w-0 rounded-md border border-line bg-cream px-3 py-2.5 text-sm ${FOKUS_RING}`}
            />
          </label>
          <label className="block text-xs text-ink-soft">
            Autor <span className="text-ink-faint">(wenn bekannt)</span>
            <input
              type="text"
              value={nfAutor}
              maxLength={255}
              onChange={(e) => setNfAutor(e.target.value)}
              placeholder="Wer hat es geschrieben?"
              className={`mt-1 block min-h-[44px] w-full min-w-0 rounded-md border border-line bg-cream px-3 py-2.5 text-sm ${FOKUS_RING}`}
            />
          </label>

          <div className="flex flex-wrap items-center gap-2">
            {/* Natürliche Breite wie in der Treffer-Vorschau darunter —
                nicht `flex-1`. Geteilt bekam „Position anlegen" auf
                390 Pixeln nur 168 Pixel und brach mitten im Wort um. */}
            <Button
              variant="primary"
              onClick={nachfrageUebernehmen}
              disabled={nfTitel.trim() === ''}
            >
              Position anlegen
            </Button>
            {/* Abbrechen legt nichts an — ausdrücklich, damit ein
                versehentlicher Scan folgenlos bleibt. */}
            <Button
              variant="ghost"
              onClick={() => {
                nachfrageSchliessen();
                inputRef.current?.focus();
              }}
            >
              Abbrechen
            </Button>
            <span className="hidden text-xs text-ink-faint md:inline">
              Enter legt an, Esc verwirft.
            </span>
          </div>
        </div>
      )}

      {/* Der Katalogtreffer (K3).
          ------------------------------------------------------------
          Derselbe Kasten an derselben Stelle wie die VLB-Vorschau, aus
          denselben drei Gründen wie bei der E9-Rückfrage oben: die
          Augen sind unter dem Feld, die Esc-Regelung von B50 gilt mit,
          und ein Dialog im Sheet wäre ein zweiter Fokus-Käfig. */}
      {katalogTreffer && (
        <div className="space-y-2 rounded-md border border-akzent-linie bg-akzent-sanft p-3">
          <div className="flex gap-3">
            {katalogTreffer.isbn !== null && (
              <Cover isbn={katalogTreffer.isbn} className="h-20 w-14" />
            )}
            <div className="flex flex-col gap-0.5 text-sm">
              <p className="font-serif text-base text-ink">{katalogTreffer.titel}</p>
              <p className="text-xs text-ink-faint">
                Nr. {katalogTreffer.nummer} · {katalogTreffer.katalog_label}
                {katalogTreffer.katalogseite !== null &&
                  `, Seite ${katalogTreffer.katalogseite}`}
              </p>
              {katalogTreffer.autor && (
                <p className="text-ink-soft">{katalogTreffer.autor}</p>
              )}
              {katalogTreffer.verlag && (
                <p className="text-ink-faint">{katalogTreffer.verlag}</p>
              )}
              {katalogTreffer.isbn !== null ? (
                <div className="flex items-center gap-1 text-xs text-ink-faint">
                  <span className="tabular-nums">
                    ISBN {isbnAnzeige(katalogTreffer.isbn)}
                  </span>
                  <CopyButton
                    value={katalogTreffer.isbn}
                    label={`ISBN ${katalogTreffer.isbn} kopieren`}
                  />
                </div>
              ) : (
                // Sieben Titel des Herbstkatalogs haben keine ISBN — die
                // Position entsteht dann mit Titel und ohne Nummer (K3).
                // Angesagt wird das VOR der Übernahme, nicht hinterher.
                <p className="text-xs text-ink-faint">
                  Ohne ISBN im Katalog — die Position entsteht ohne Nummer.
                </p>
              )}
              {katalogTreffer.preis_text !== null && (
                // Der Preis ist der Stand vom Drucktermin und wird NIE
                // in die Position übernommen (Entscheidung K1). Der
                // Zusatz steht direkt daneben, damit niemand ihn im
                // Preisfeld sucht.
                <p className="text-ink-soft">
                  {katalogTreffer.preis_text}{' '}
                  <span className="text-xs text-ink-faint">
                    (Katalogpreis, wird nicht übernommen)
                  </span>
                </p>
              )}
              {!katalogTreffer.aktiv && (
                <p className="mt-1 text-xs text-gold-dark">
                  aus {katalogTreffer.katalog_label}, Preis prüfen
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" onClick={() => katalogUebernehmen(katalogTreffer)}>
              Übernehmen
            </Button>
            <Button variant="ghost" onClick={() => setKatalogTreffer(null)}>
              Abbrechen
            </Button>
            <span className="text-xs text-ink-faint">
              Enter übernimmt, Esc verwirft.
            </span>
          </div>
        </div>
      )}

      {review && (
        <div
          className={`space-y-2 rounded-md border p-3 ${
            review.digital ? 'border-coral bg-cream-warm' : 'border-akzent-linie bg-akzent-sanft'
          }`}
        >
          {review.digital && (
            <div className="space-y-1" data-testid="digital-warnung">
              <DigitalMarke />
              <p className="text-sm text-coral">
                Das ist eine digitale Ausgabe zum Herunterladen. Die gedruckte
                Ausgabe hat eine eigene ISBN.
              </p>
            </div>
          )}
          <div className="flex gap-3">
            <Cover isbn={review.isbn} className="h-20 w-14" />
            <div className="flex flex-col gap-0.5 text-sm">
              <p className="font-serif text-base text-ink">{review.titel}</p>
              <div className="flex items-center gap-1 text-xs text-ink-faint">
                <span className="tabular-nums">ISBN {isbnAnzeige(review.isbn)}</span>
                <CopyButton value={review.isbn} label={`ISBN ${review.isbn} kopieren`} />
              </div>
              {review.autor && <p className="text-ink-soft">{review.autor}</p>}
              {review.verlag_text && (
                <p className="text-ink-faint">
                  {review.verlag_text}
                  {review.erscheinungsjahr ? ` · ${review.erscheinungsjahr}` : ''}
                </p>
              )}
              {review.preis_cent !== null && (
                <p className="text-ink-soft">
                  {(review.preis_cent / 100).toLocaleString('de-DE', {
                    style: 'currency',
                    currency: 'EUR'
                  })}
                </p>
              )}
              {/* A24 (#61): Gibt es das Buch ueberhaupt noch? Die Zeile
                  steht unter den Titeldaten und ueber den Knoepfen — auf
                  dem Weg des Blickes von „was ist das" zu „nehme ich
                  es". */}
              <div className="mt-1">
                <LieferbarkeitZeile wert={review.lieferbarkeit} standVom={reviewStand} />
              </div>
            </div>
          </div>
          {review.digital ? (
            <div className="flex flex-wrap items-center gap-2">
              {review.titel && (
                <Button
                  variant="primary"
                  onClick={() => {
                    setSucheVorgabe(review.titel);
                    setTitelSucheOffen(true);
                    setReview(null);
                    setReviewStand(null);
                  }}
                >
                  Gedruckte Ausgabe suchen
                </Button>
              )}
              <Button
                variant={review.titel ? 'secondary' : 'primary'}
                onClick={() => {
                  setReview(null);
                  setReviewStand(null);
                  focusInput();
                }}
              >
                Verwerfen
              </Button>
              <Button variant="ghost" onClick={() => uebernehmen(review)}>
                Trotzdem übernehmen
              </Button>
              <span className="text-xs text-ink-faint">Esc verwirft.</span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="primary" onClick={() => uebernehmen(review)}>
                Übernehmen
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setReview(null);
                  setReviewStand(null);
                }}
              >
                Abbrechen
              </Button>
              <span className="text-xs text-ink-faint">
                Enter übernimmt, Esc verwirft.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
